import { useEffect, useMemo, useRef, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useWindow } from '@krp/client-runtime'
import {
  NO_SCAN,
  platformCatalogProducts,
  scanCleared,
  scanSessionStarted,
  scanned,
  SCANNED_PRODUCTS_LIMIT,
  ScannerScreen,
  toBarcodeLookup,
  type ScannerActions,
  type ScannerVm,
  type ScanState,
} from '@krp/platform-ui'

import { useAppDispatch } from '../../app/hooks'
import { SearchTimingsBinding } from './SearchTimingsBinding'
import { usePhotoSearch } from './usePhotoSearch'
import { productPhotoUrl } from './useProductLinks'
import { useScanFlow } from './useScanFlow'

/**
 * Binding сканера поиска по фото (RP-FB4). Штрихкод — запрос в общий каталог, снимок — тот же поиск по фото, что на
 * экране поиска. Свежее заменяет прежнее: новый код гасит прошлую выдачу по фото, снимок — прошлый код. Код живёт,
 * пока открыт сканер (срок `screen`): это ответ камеры, а не работа человека.
 *
 * Что считать НОВЫМ кодом, решает экран (другой код или тот же после ухода из кадра), сюда доходят только новые.
 * Каждое сканирование получает порядковый номер `id`: по нему лист результата открывается заново, даже скрытый и даже
 * для того же кода. Гонка ответов закрыта ключом запроса: ответ каталога принадлежит своему коду, и `currentData`
 * отдаёт только ответ на ТЕКУЩИЙ код — запоздавший ответ прежнего кода на экран не попадает.
 *
 * Сканирование как пополнение базы (RP-FB56): ответ по фото несёт вердикт; «Создать новый» и «Прикрепить фото» открывают окна
 * поверх сканера, а счётчик смены (создано, прикреплено, найдено, пропущено) начинается заново при каждом открытии сканера.
 * Создали или прикрепили — прежний ответ сбрасывается, и сканер возвращается к видоискателю, готовый к следующему кадру.
 */
export function PhotoScannerBinding() {
  const { close } = useWindow()
  const { vm: search, actions: searchActions, reset, sentShot } = usePhotoSearch()
  const dispatch = useAppDispatch()
  const answer = search.outcome.kind === 'verdict' ? search.outcome : null
  const flow = useScanFlow(answer?.hints ?? null, answer?.searchId ?? null)
  const { session } = flow
  const [scan, setScan] = useState<ScanState>(NO_SCAN)
  const code = scan.code
  const lookup = platformCatalogProducts.useQuery(code === '' ? skipToken : { query: code, page: 1, pageSize: SCANNED_PRODUCTS_LIMIT })
  // `currentData`, а не `data`: при новом коде прежний ответ не должен выглядеть ответом на него.
  const current = { ...lookup, data: lookup.currentData }
  const vm: ScannerVm = { scanId: scan.id, lookup: toBarcodeLookup(code, current), photo: search.outcome }

  // Новая смена начинается с открытием сканера; окна поверх него (создание, прикрепление) сканер не закрывают и смену не сбрасывают.
  useEffect(() => {
    dispatch(scanSessionStarted())
  }, [dispatch])

  // Товар создан или фото прикреплено — прежний ответ больше не нужен: сканер снова показывает видоискатель.
  const lastId = session.last?.id ?? 0
  const lastKind = session.last?.kind
  const seenId = useRef(lastId)
  useEffect(() => {
    if (lastId === seenId.current) return
    seenId.current = lastId
    if (lastKind === 'created' || lastKind === 'attached') reset()
  }, [lastId, lastKind, reset])

  const base = useMemo<ScannerActions>(
    () => ({
      // Функциональное обновление: действия не зависят от кода, декодер камеры не перезапускается на каждом скане.
      code: (next) => {
        // Идущий поиск по снимку прерывается: его запоздалый ответ не должен лечь поверх результата нового кода.
        searchActions.cancel?.()
        reset()
        setScan((state) => scanned(state, next))
      },
      photo: (file) => {
        setScan(scanCleared)
        searchActions.search(file)
      },
      cancel: () => searchActions.cancel?.(),
      retry: () => searchActions.retry?.(),
      close,
    }),
    [close, reset, searchActions],
  )
  const actions = useMemo<ScannerActions>(() => ({ ...base, verdict: flow.verdict }), [base, flow.verdict])

  return (
    <ScannerScreen
      vm={vm}
      actions={actions}
      photoUrl={productPhotoUrl}
      onOpenProduct={flow.openProduct}
      sentShot={sentShot}
      canSeeDetails={search.isSystemAdmin}
      detailsExtra={<SearchTimingsBinding searchId={answer?.searchId ?? null} />}
      session={session}
    />
  )
}
